/**
 * Initializes custom faux select boxes.
 * Handles interaction, accessibility (ARIA), and keyboard navigation.
 * 
 * @param {HTMLElement|Document} context - The container to scope the initialization to.
 */
export function initializeFauxSelects(context = document) {
    const selects = context.querySelectorAll('.faux-select');

    selects.forEach(select => {
        // Prevent double initialization
        if (select.classList.contains('js-faux-select-init')) return;
        select.classList.add('js-faux-select-init');

        const trigger = select.querySelector('.faux-select__trigger');
        const list = select.querySelector('.faux-select__options');
        const options = select.querySelectorAll('.faux-select__option');
        const wrapper = select.closest('.faux-select-wrapper');
        const nativeSelect = wrapper ? wrapper.querySelector('select') : null;
        const labelSpan = select.querySelector('.faux-select__label');

        if (!trigger || !list || !nativeSelect) return;

        // Toggle Menu
        trigger.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation(); // Prevent document click from closing immediately
            
            const isOpen = select.classList.contains('is-open');
            
            // Close all other selects
            closeAllFauxSelects(select);

            if (isOpen) {
                closeSelect(select, trigger);
            } else {
                openSelect(select, trigger);
            }
        });

        // Option Click
        options.forEach(option => {
            option.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                const clickedValue = this.getAttribute('data-value');
                const isAlreadySelected = this.classList.contains('is-selected');
                
                let newValue = clickedValue;
                let newText = this.textContent;
                let optionToSelect = this;
                let isDeselecting = false;

                // If clicking the currently selected option, and it's not the default (empty) one,
                // revert to the default option.
                if (isAlreadySelected && clickedValue !== "") {
                    isDeselecting = true;
                    // We are deselecting.
                    newValue = "";
                    
                    // Find the default text from the native select
                    const defaultNativeOption = Array.from(nativeSelect.options).find(opt => opt.value === "") || nativeSelect.options[0];
                    
                    if (defaultNativeOption) {
                        newText = defaultNativeOption.text;
                        newValue = defaultNativeOption.value;
                    }

                    // Check if there is a faux option for the default value
                    const defaultFauxOption = Array.from(options).find(opt => opt.getAttribute('data-value') === newValue);
                    
                    optionToSelect = defaultFauxOption || null;
                }

                // Update Native Select
                nativeSelect.value = newValue;
                // Trigger change event on native select for other listeners
                nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));

                // Update UI
                if (labelSpan) {
                    if (labelSpan.children.length > 0) {
                        // Preserve child elements (like icons) and only update the text
                        let textNode = Array.from(labelSpan.childNodes).find(node => node.nodeType === 3 && node.nodeValue.trim().length > 0);
                        
                        if (!textNode) {
                            textNode = Array.from(labelSpan.childNodes).find(node => node.nodeType === 3);
                        }

                        if (textNode) textNode.nodeValue = newText;
                        else labelSpan.prepend(document.createTextNode(newText));
                    } else {
                        labelSpan.textContent = newText;
                    }
                }
                
                // Update Aria/Classes
                options.forEach(opt => {
                    opt.classList.remove('is-selected');
                    opt.setAttribute('aria-selected', 'false');
                });
                
                if (optionToSelect) {
                    optionToSelect.classList.add('is-selected');
                    optionToSelect.setAttribute('aria-selected', 'true');
                }

                if (!isDeselecting) {
                    closeSelect(select, trigger);
                    trigger.focus();
                }
            });
        });

        // Keyboard Navigation
        select.addEventListener('keydown', function(e) {
            // Support for Escape, Enter, Space, Arrows
            if (e.key === 'Escape') {
                closeSelect(select, trigger);
                trigger.focus();
            } else if (e.key === 'Enter' || e.key === ' ') {
                // If on trigger, toggle. If on option, select.
                if (document.activeElement === trigger) {
                    e.preventDefault();
                    if (select.classList.contains('is-open')) {
                        closeSelect(select, trigger);
                    } else {
                        openSelect(select, trigger);
                    }
                } else if (document.activeElement.classList.contains('faux-select__option')) {
                    e.preventDefault();
                    document.activeElement.click();
                }
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                if (!select.classList.contains('is-open')) {
                    openSelect(select, trigger);
                } else {
                    const focusableOptions = Array.from(options);
                    const currentIndex = focusableOptions.indexOf(document.activeElement);
                    let nextIndex = 0;

                    if (e.key === 'ArrowDown') {
                        nextIndex = (currentIndex + 1) % focusableOptions.length;
                    } else {
                        nextIndex = (currentIndex - 1 + focusableOptions.length) % focusableOptions.length;
                    }
                    focusableOptions[nextIndex].focus();
                }
            }
        });
    });
}

function openSelect(select, trigger) {
    select.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    
    // Focus first selected or first option
    const selected = select.querySelector('.faux-select__option.is-selected');
    const first = select.querySelector('.faux-select__option');
    if (selected) {
        setTimeout(() => selected.focus(), 50);
    } else if (first) {
        setTimeout(() => first.focus(), 50);
    }
}

function closeSelect(select, trigger) {
    select.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
}

function closeAllFauxSelects(exceptSelect = null) {
    document.querySelectorAll('.faux-select.is-open').forEach(s => {
        if (s !== exceptSelect) {
            s.classList.remove('is-open');
            const t = s.querySelector('.faux-select__trigger');
            if (t) t.setAttribute('aria-expanded', 'false');
        }
    });
}

// Global click to close
document.addEventListener('click', function(e) {
    if (!e.target.closest('.faux-select')) {
        closeAllFauxSelects();
    }
});